/**
 * Frontend Command Registry — modules register context-aware actions once;
 * the palette itself never changes when a module is added.
 *
 *   import { registerContextProvider } from "../command/registry";
 *   registerContextProvider({ match: (path) => ..., actions: (path) => [...] });
 */
const contextProviders = [];

export const registerContextProvider = (provider) => {
  contextProviders.push(provider);
};

export const contextActionsFor = (pathname) => {
  const out = [];
  for (const p of contextProviders) {
    try {
      const m = p.match(pathname);
      if (m) out.push(...(p.actions(m, pathname) || []));
    } catch { /* provider errors never break the palette */ }
  }
  return out;
};

const act = (title, url, icon = "arrow-right") =>
  ({ id: `ctx:${url}:${title}`, title, url, icon, group: "THIS PAGE", subtitle: "context action", actions: [], score: 50 });

/** Current-page context sent with every Oracle ask so "review this
 *  investigation" resolves without naming the record. */
export const pageContextFor = (pathname) => {
  let m = pathname.match(/^\/topic\/([^/#?]+)/);
  if (m) return { page_type: "dossier", slug: m[1] };
  m = pathname.match(/^\/(epstein-files\/entity|entity|connections|people|organizations|operations)\/([^/#?]+)/);
  if (m) return { page_type: "entity", slug: m[2] };
  if (pathname.startsWith("/admin/media")) return { page_type: "media" };
  if (pathname.startsWith("/admin/security")) return { page_type: "security" };
  if (pathname.startsWith("/admin")) return { page_type: "admin" };
  if (pathname.startsWith("/members")) return { page_type: "members" };
  return {};
};

/* Built-in context providers */
registerContextProvider({
  match: (p) => p.match(/^\/topic\/([^/]+)/),
  actions: (m) => {
    const s = m[1];
    return [
      { id: "watch:toggle", title: "Watch / Unwatch This Investigation",
        url: null, icon: "eye", group: "THIS PAGE",
        subtitle: "Oracle monitors changes and reports them", actions: [], score: 51 },
      act("Open Timeline", `/topic/${s}#timeline`, "clock"),
      act("Evidence Library", `/topic/${s}#evidence`, "file-text"),
      act("Sources", `/topic/${s}#sources`, "link"),
      act("Comments & Debate", `/topic/${s}#comments`, "message-square"),
    ];
  },
});

registerContextProvider({
  match: (p) => p.match(/^\/(epstein-files\/entity|entity|connections)\/([^/]+)/),
  actions: (m) => {
    const s = m[2];
    return [
      act("Connections Graph", `/connections/${s}`, "network"),
      act("Network Timeline", `/epstein-files/timeline/${s}`, "clock"),
      act("Archive Profile", `/entity/${s}`, "user"),
    ];
  },
});

registerContextProvider({
  match: (p) => p.startsWith("/admin/media"),
  actions: () => [
    act("Unused Assets", "/admin/media?health=unused", "image"),
    act("Review Ready", "/admin/media?health=review_ready", "check-square"),
    act("Missing Captions", "/admin/media?health=missing_caption", "pen-line"),
  ],
});

registerContextProvider({
  match: (p) => p.startsWith("/admin"),
  actions: () => [act("Security Center", "/admin/security/center", "shield")],
});
